import { Select, Spin } from 'antd'
import { useEffect } from 'react'

import { useFilterLabels } from 'src/context'

export type FacetOption = { value: string; label: string }

// How many facet suggestions to show before the user starts typing. Searching
// still matches the full list — this only caps the initial browse so a long
// list never forces scrolling.
export const FACET_VISIBLE_LIMIT = 15

// Build capped, searchable dropdown options from a set of distinct values.
// Typeahead: cap the initial (no-search) suggestions, but search the COMPLETE
// list so any value stays findable. The selected value is always kept present
// (with its label), even before the list loads or if it is momentarily absent.
export const buildFacetOptions = (
  values: string[] | undefined,
  selected: string,
  search: string,
  getLabel: (value: string) => string
): FacetOption[] => {
  // Copy so the unshift below never mutates the cached query data.
  const pool = [...(values ?? [])]
  if (selected && !pool.includes(selected)) {
    pool.unshift(selected)
  }

  const all = pool
    .map((value) => ({ value, label: getLabel(value) }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const query = search.trim().toLowerCase()
  const shown = query
    ? all.filter((option) => option.label.toLowerCase().includes(query))
    : all.slice(0, FACET_VISIBLE_LIMIT)

  const selectedOption = all.find((option) => option.value === selected)

  return selectedOption &&
    !shown.some((option) => option.value === selectedOption.value)
    ? [selectedOption, ...shown]
    : shown
}

type FilterSelectProps = {
  value: string
  options: FacetOption[]
  onSelect: (value: string) => void
  onSearch: (text: string) => void
  placeholder: string
  width: number | string
  loading?: boolean
  filterKey?: string
}

/**
 * Shared typeahead filter dropdown: the search box drives `onSearch` (a
 * client-side facet or a server fetch) and selecting a value clears it.
 * `filterOption={false}` because the caller owns filtering — AntD must not
 * also filter the options it is handed.
 */
export const FilterSelect = ({
  value,
  options,
  onSelect,
  onSearch,
  placeholder,
  width,
  loading,
  filterKey
}: FilterSelectProps) => {
  const filterLabels = useFilterLabels()

  useEffect(() => {
    if (filterKey) filterLabels?.registerOptions(filterKey, options)
  }, [filterKey, filterLabels, options])

  return (
    <Select<string>
      size="small"
      showSearch
      allowClear
      filterOption={false}
      value={value || undefined}
      options={options}
      onChange={(next) => {
        onSelect(next ?? '')
        onSearch('')
      }}
      onSearch={onSearch}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : undefined}
      placeholder={placeholder}
      style={{ width }}
    />
  )
}
